import { COLOR_LIST, ColorTrait } from "@/data/colors";

export type MatrixCategory = "fears" | "aspirations" | "hesitations";

/** ~20 colors evenly spread across the full 175-color library (by index),
 *  so the picker spans every family instead of clustering in one hue range. */
export function getMatrixPalette(count = 20): ColorTrait[] {
  const n = COLOR_LIST.length;
  const step = n / count;
  const out: ColorTrait[] = [];
  for (let i = 0; i < count; i++) {
    out.push(COLOR_LIST[Math.floor(i * step)]);
  }
  return out;
}

const CATEGORY_LABEL: Record<MatrixCategory, string> = {
  fears: "Fears",
  aspirations: "Aspirations",
  hesitations: "Hesitations",
};

const CATEGORY_TEMPLATES: Record<MatrixCategory, (color: ColorTrait) => string> = {
  fears: (c) =>
    `The fear underneath this choice wears ${c.name.toLowerCase()}. ${c.meaning} In practice, that usually means what worries you isn't the decision itself — it's ${c.keywords[0].toLowerCase()} slipping out of your hands, or having too much of it forced on you at once.`,
  aspirations: (c) =>
    `What you're actually reaching for is ${c.name.toLowerCase()}. ${c.meaning} That points to a version of this outcome defined by ${c.keywords[0].toLowerCase()} and ${c.keywords[1].toLowerCase()} — not just "the right answer," but a specific feeling you're trying to create.`,
  hesitations: (c) =>
    `The hesitation itself is colored ${c.name.toLowerCase()}. ${c.decisionMeaning} That suggests the holdup isn't really indecision — it's a values conflict between what you want and what ${c.keywords[0].toLowerCase()} would cost you to get it.`,
};

export interface MatrixSelection {
  fears: ColorTrait;
  aspirations: ColorTrait;
  hesitations: ColorTrait;
}

export interface MatrixInsight {
  category: MatrixCategory;
  label: string;
  color: ColorTrait;
  text: string;
}

export interface MatrixAnalysis {
  insights: MatrixInsight[];
  synthesis: string;
}

/** Pure function: given the three chosen colors (+ optional dilemma text for
 *  flavor), produce a category-by-category reading plus a synthesis
 *  paragraph. No randomness — same inputs always produce the same reading. */
export function analyzeMatrix(selection: MatrixSelection, dilemma: string): MatrixAnalysis {
  const insights: MatrixInsight[] = (Object.keys(selection) as MatrixCategory[]).map((category) => {
    const color = selection[category];
    return {
      category,
      label: CATEGORY_LABEL[category],
      color,
      text: CATEGORY_TEMPLATES[category](color),
    };
  });

  const { fears, aspirations, hesitations } = selection;
  const dilemmaClause = dilemma.trim() ? ` on "${dilemma.trim()}"` : "";

  const sameFamily = fears.family === aspirations.family;
  const synthesis = sameFamily
    ? `Your fear and your aspiration draw from the same emotional register${dilemmaClause} — ${fears.name} and ${aspirations.name} sit close together. That's a sign the choice is less about opposing outcomes and more about degree: how much ${fears.keywords[0].toLowerCase()} you're willing to hold at once. ${hesitations.name} marks where that balance still feels unresolved.`
    : `There's real distance between what you fear (${fears.name}) and what you're hoping for (${aspirations.name})${dilemmaClause} — one pulls toward ${fears.keywords[0].toLowerCase()}, the other toward ${aspirations.keywords[0].toLowerCase()}. ${hesitations.name} is the color of the gap between them: it's not that you don't know what you want, it's that getting it means confronting what you're afraid of directly.`;

  return { insights, synthesis };
}
